import React, { useState, useEffect } from 'react'

const FAQ: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const faqs = [
    {
      question: 'What is BMWFinder?',
      answer: 'BMWFinder is a specialized platform that scans every BMW at dealerships across the United States. We cover E, F, and G chassis models and decode each VIN to verify options like individual colors, bucket seats, and competition packages.'
    },
    {
      question: 'How does VIN decoding work?',
      answer: 'Every car in our inventory has its VIN run through BMW\'s official decoder. This lets us confirm factory-optioned equipment like bucket seats, competition packages, and individual paint codes, so you never waste time on incorrectly listed vehicles.'
    },
    {
      question: 'How often is inventory updated?',
      answer: 'Inventory is refreshed on a regular cycle. Everyone gets access to the full database, including the latest listings as they appear.'
    },
    {
      question: 'Which BMW models do you track?',
      answer: 'We track BMWs across E chassis, F chassis, and G chassis generations. Each listing is VIN-decoded to verify special equipment like individual colors, bucket seats, and competition packages.'
    },
    {
      question: 'Is BMWFinder free?',
      answer: 'Yes. The complete inventory, VIN decoding, filters, and distance sorting are available free with no account required.'
    },
  ]

  useEffect(() => {
    setIsVisible(true)
  }, [])

  const toggleFaq = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index)
  }

  return (
    <div id="faq" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
      {/* Racing stripe divider */}
      <div className="mb-16">
        <div className="h-[2px] w-full bg-gradient-to-r from-rose-600 via-rose-600/40 to-transparent" />
      </div>

      {/* 4/8 column split */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">

        {/* Left — 4 cols, sticky */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24">
            <span className="font-mono text-xs text-rose-500 uppercase tracking-widest mb-3 block">FAQ</span>
            <h2
              className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-left mb-4 transform transition-all duration-500 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
              }`}
              style={{ letterSpacing: '-0.03em' }}
            >
              Common<br />questions.
            </h2>
            <p
              className={`text-neutral-400 text-sm leading-relaxed mb-6 max-w-xs transform transition-all duration-500 delay-100 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
              }`}
            >
              Everything you need to know about BMWFinder.{' '}
              <a
                href="/contact"
                className="text-rose-500 hover:text-rose-400 transition-colors no-underline border-b border-rose-500/30 hover:border-rose-400/50"
              >
                Contact us
              </a>{' '}
              if you can't find your answer.
            </p>
          </div>
        </div>

        {/* Right — 8 cols: numbered FAQ entries */}
        <div className="lg:col-span-8">
          <div className="border-t border-white/10">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border-b border-white/10"
              >
                <button
                  className="w-full py-5 sm:py-6 text-left focus:outline-none bg-transparent border-none cursor-pointer flex items-start gap-4 sm:gap-5 group"
                  onClick={() => toggleFaq(index)}
                >
                  {/* Number */}
                  <span className="font-mono text-sm text-neutral-600 mt-0.5 flex-shrink-0 w-6 text-right">
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Question */}
                  <h3
                    className={`text-base sm:text-lg font-semibold flex-1 transition-colors duration-300 pr-4 ${
                      activeIndex === index ? 'text-rose-500' : 'text-white group-hover:text-neutral-300'
                    }`}
                  >
                    {faq.question}
                  </h3>

                  {/* Plus/minus toggle in circle */}
                  <div
                    className={`w-7 h-7 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-300 ${
                      activeIndex === index
                        ? 'border-rose-600/50 bg-rose-600/10'
                        : 'border-white/20 bg-transparent group-hover:border-white/30'
                    }`}
                  >
                    <svg
                      className={`w-3.5 h-3.5 transition-all duration-300 ${
                        activeIndex === index ? 'text-rose-500' : 'text-neutral-500'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {activeIndex === index ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      )}
                    </svg>
                  </div>
                </button>

                {/* Answer */}
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: activeIndex === index ? '500px' : '0' }}
                >
                  <div className="pl-10 sm:pl-11 pb-5 sm:pb-6 pr-12 text-neutral-400 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FAQ
